import React from "react";
import styled from "styled-components";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { colors } from "app/popleads/components/ui-kit/colors";
import MaterialIcon from "app/popleads/components/ui-kit/MaterialIcon";
import Spinner from "app/popleads/components/ui-kit/Spinner";

// Shared look of an AI-suggested email (profile card and About panel): a spark icon, the address in the
// L3RN Tech gradient and the "AI" badge. Muted when the address already belongs to another profile. No animation.

const Spark = styled(MaterialIcon)`
    color: ${colors.l3rnTech[500]};
`;

export const AiSpark = ({ fontSize = "16px", title }) => (
    <Spark icon="auto_awesome" fontSize={fontSize} title={title} />
);

const Row = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: 100%;
`;

// Clicking the address saves it to the profile (onSave); without onSave it is plain text.
const Address = styled.span`
    min-width: 0;
    cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 500;
    background: linear-gradient(90deg, ${colors.l3rnTech[900]}, ${colors.l3rnTech[400]});
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    &:hover {
        text-decoration: underline;
        text-decoration-color: ${colors.l3rnTech[400]};
    }
    ${({ $muted }) =>
        $muted &&
        `
        background: none;
        color: ${colors.neutral[500]};
        text-decoration: line-through;
    `}
`;

// The kit has no pink; the AI badge's pink → violet → blue gradient is specific to this mark.
const AI_PINK = "#F0379B";

const AI_GRADIENT = `linear-gradient(135deg, ${AI_PINK} 0%, ${colors.other[900]} 50%, ${colors.primary[900]} 100%)`;

// Outlined mark: white tile, gradient frame, gradient "AI".
const Badge = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    min-width: 22px;
    padding: 0 4px;
    box-sizing: border-box;
    border: 1.5px solid transparent;
    border-radius: 5px;
    background: linear-gradient(${colors.white}, ${colors.white}) padding-box, ${AI_GRADIENT} border-box;
`;

const BadgeText = styled.span`
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.3px;
    line-height: 1;
    background: ${AI_GRADIENT};
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
`;

const AiBadge = () => (
    <Badge aria-label="AI">
        <BadgeText>AI</BadgeText>
    </Badge>
);

// Verification result (email_verification.status) -> mark next to the badge
const VERIFICATION = {
    ok: { icon: "check", color: colors.success[900], text: "This email is verified" },
    invalid: { icon: "cancel", color: colors.danger[900], text: "Not verified: the mailbox does not exist" },
    disposable: { icon: "cancel", color: colors.danger[900], text: "Not verified: disposable address" },
    catch_all: { text: "Unconfirmed: the domain accepts every address" },
    unknown: { text: "Unconfirmed: the mail server did not answer" },
    error: { text: "Not checked yet" },
};

const Unconfirmed = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid ${colors.neutral[400]};
    color: ${colors.neutral[600]};
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
`;

// Unconfirmed, with the share of the company's known addresses that use this pattern instead of the "?"
const Likelihood = styled(Unconfirmed)`
    width: auto;
    padding: 0 4px;
    border-radius: 7px;
    letter-spacing: 0.2px;
`;

// Verified: green check in a green-outlined circle, drawn as one SVG so the check is optically centred
// (the font glyph sits low and right inside its own box).
const VerifiedIcon = () => (
    <svg
        width="15"
        height="15"
        viewBox="0 0 16 16"
        aria-hidden="true"
        overflow="visible"
        style={{ flexShrink: 0, display: "block", overflow: "visible", marginRight: 1, color: colors.success[900] }}
    >
        <circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
        <path
            d="M4.9 8.05 L7.1 10.15 L11.1 5.85"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Same tooltip as the extension's other row icons ("Copy Email", "Add Email"); a span carries the hover.
const MarkTip = ({ text, children }) => (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
        <span aria-label={text} style={{ display: "inline-flex", flexShrink: 0, cursor: "default" }}>
            {children}
        </span>
    </OverlayTrigger>
);

const VerificationMark = ({ verification, verifying, likelihood }) => {
    if (verifying && !verification) {
        return (
            <MarkTip text="Verifying this email…">
                <Spinner size={12} thickness={2} variant="l3rnTech" decorative />
            </MarkTip>
        );
    }
    const share = likelihood
        ? `${likelihood.percent}% of the ${likelihood.known} known addresses at this company use this format.`
        : null;
    const mark = verification && VERIFICATION[verification.status];
    if (!mark) {
        // not checked (yet): the pattern's share is the only signal
        if (!share) return null;
        return (
            <MarkTip text={`Not verified. ${share}`}>
                <Likelihood>{likelihood.percent}%</Likelihood>
            </MarkTip>
        );
    }
    let icon;
    let text = mark.text;
    if (verification.status === "ok") icon = <VerifiedIcon />;
    else if (!mark.icon && share) {
        icon = <Likelihood>{likelihood.percent}%</Likelihood>;
        text = `${mark.text}. ${share}`;
    } else if (!mark.icon) icon = <Unconfirmed>?</Unconfirmed>;
    else icon = <MaterialIcon icon={mark.icon} fontSize="15px" style={{ color: mark.color }} />;
    return <MarkTip text={text}>{icon}</MarkTip>;
};

/**
 * The address with its AI badge and verification mark; a tooltip explains the suggestion on hover.
 * A mailbox the verifier reported as missing is shown muted, like an address owned by another profile.
 * likelihood ({ percent, known }, see patternLikelihood) replaces the "?" of an unconfirmed address.
 */
export const AiEmailText = ({ email, muted, verification, verifying, likelihood, onSave }) => {
    const rejected = ["invalid", "disposable"].includes(verification?.status);
    const clickable = Boolean(onSave) && !muted;
    const save = () => clickable && onSave(email);
    return (
        <Row>
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        {muted ? "Already used by another profile" : "Click to save this email to the profile"}
                    </Tooltip>
                }
            >
                <Address
                    $muted={muted || rejected}
                    $clickable={clickable}
                    role={clickable ? "button" : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={save}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            save();
                        }
                    }}
                >
                    {email}
                </Address>
            </OverlayTrigger>
            <AiBadge />
            {!muted && <VerificationMark verification={verification} verifying={verifying} likelihood={likelihood} />}
        </Row>
    );
};
